import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import { SubscriptionGrid } from "./SubscriptionGrid";
import { InvoiceTable } from "./InvoiceTable";
import {
    SilverSunnStripeSubscription,
    StripePrice,
    StripePriceAnonymous
} from "@switcherstudio/switcher-api-client";
import { AppDispatch } from "store/store";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { displayDate } from "helpers/time";
import { useTranslation } from "react-i18next";
import rollbar from "helpers/rollbar";
import {
    mustBeSubscriptionOwner,
    useRedirectIfDisallowed
} from "hooks/useRedirectIfDisallowed";
import { StripeStatus } from "../platforms/types";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { NextPaymentOn } from "./NextPaymentOn";
import styles from "./index.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { ManagePlanModal } from "components/modal/ManagePlanModal";
import { setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { EntitlementGrid } from "./EntitlementGrid";
import { useGetStripePrice } from "hooks/useGetStripePrice";
import { useGetLastClaimedCoupon } from "hooks/useGetLastClaimedCoupon";
import { SetupIntentElement } from "components/stripe/SetupIntentElement";
import { useCheckoutRedirect } from "hooks/useCheckoutRedirect";
import {
    refreshToken,
    getUserInfo,
    getUserOrgs,
    getDefaultUserOrg
} from "store/user/thunks";
import { useHasPlanRole } from "hooks/useHasPlanRole";
import { first, last } from "helpers/arrays";
import { useUserInvoices } from "hooks/useUserInvoices";
import { useInterval } from "hooks/useInterval";
import { PauseAction } from "hooks/usePause";
import { LoadingOverlay } from "components/loading-overlay";

const pausedProductId = import.meta.env.VITE_PAUSED_PRODUCT_ID;

export interface SubscriptionWithPrice {
    subscription?: SilverSunnStripeSubscription;
    price?: StripePrice;
}

export const SubscriptionPage: React.FC = () => {
    const { hash, search } = useLocation();

    // Extract couponCode and planId from the query parameters
    const searchParams = useMemo<URLSearchParams>(
        () => new URLSearchParams(search),
        [search]
    );

    const [couponCode, setCouponCode] = useState(
        searchParams.get("couponCode")
    );
    const planId = useMemo(() => {
        // Only return plan if redirect_status is not present meaning we are handling a checkout
        if (!searchParams.get("redirect_status")) {
            return searchParams.get("planId")?.toUpperCase();
        }
    }, [searchParams]);

    const hideSubscriptionPlans =
        searchParams.get("hideSubscriptions") === "true";

    const isGrowUser = useHasPlanRole("Grow");

    useRedirectIfDisallowed(mustBeSubscriptionOwner);

    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const dispatch = useDispatch<AppDispatch>();
    const { userInfo } = useSelector((state: RootState) => state.user);

    const [hasActiveSub, setHasActiveSub] = useState<boolean>(false);
    const [stripePrice, setStripePrice] = useState<StripePrice>();
    const [detailedSubscriptions, setDetailedSubscriptions] =
        useState<SubscriptionWithPrice[]>(undefined);
    const { claimedCoupon } = useGetLastClaimedCoupon({
        couponCode: couponCode
    });
    const [preselectedPrice, setPreselectedPrice] = useState<
        StripePrice | StripePriceAnonymous
    >();
    // Use local loading state to prevent race condition when opening modal on page load
    const [pricesLoadingLocal, setPricesLoadingLocal] = useState<boolean>(true);
    const anonymousPlan = useGetStripePrice(planId)?.plan;

    const { data: prices, dispatchApiRequest: getStripePrices } =
        useSwitcherClient((client) => client.userStripePrices_GetStripePrices, {
            args: [userInfo?.User?.Details?.Id, i18n?.language?.slice(0, 2)],
            requestImmediately: true,
            preFetch: () => setPricesLoadingLocal(true),
            onSuccess: () => setPricesLoadingLocal(false),
            onError: () => setPricesLoadingLocal(false)
        });
    const [filteredPrices, setFilteredPrices] = useState<StripePrice[]>(prices);

    const {
        paidInvoices,
        upcomingDraftInvoice,
        invoicesLoading,
        InvoiceDateRangeSelectorComponent,
        refresh: refreshInvoices
    } = useUserInvoices({});

    const { dispatchApiRequest: setPrimaryPaymentMethod } = useSwitcherClient(
        (client) => client.userPaymentMethods_SetPrimaryPaymentMethod,
        { suppressErrorBoundary: true, requestImmediately: false }
    );

    const { dispatchApiRequest: deletePaymentMethod } = useSwitcherClient(
        (client) => client.userPaymentMethods_DeletePaymentMethod,
        { suppressErrorBoundary: true, requestImmediately: false }
    );

    useEffect(() => {
        if (!prices) return;

        let localPrices = [...prices];

        /** if user does not have the app claim, filter out Studio tier plans */
        if (isGrowUser) {
            localPrices = localPrices?.filter(
                (p) =>
                    !p?.Name.includes("Switcher Studio Monthly") &&
                    !p?.Name.includes("Studio Yearly")
            );
        }

        /** if user has been redirected here to add a NON subscription plan, filter out the subscription plans */
        if (hideSubscriptionPlans) {
            localPrices = localPrices?.filter((p) => !p?.IsRecurring);
        }

        /** otherwise, get and use all available prices! */
        setFilteredPrices(localPrices);
    }, [isGrowUser, prices, hideSubscriptionPlans]);

    /** if there is a planId in the querystring params, use it to preselect the plan
     * in the modal and advance the user to the checkout step */
    useEffect(() => {
        if (!!planId) {
            const planIsPublic = prices?.find((p) => p?.Id === planId);
            if (!!planIsPublic) {
                setPreselectedPrice(planIsPublic);
                return;
            }
            if (anonymousPlan) {
                setPreselectedPrice(anonymousPlan);
                return;
            }
        }
        setPreselectedPrice(undefined);
    }, [prices, anonymousPlan, planId]);

    /** if there is a plan/price associated with the coupon in the querystring,
     * preselect that plan in the modal and advance the user to the checkout step */
    useEffect(() => {
        const priceAssociatedWithCoupon =
            claimedCoupon?.ResellerInventoryItem?.ResellerInventory?.Price;
        if (couponCode && !!priceAssociatedWithCoupon) {
            setPreselectedPrice(priceAssociatedWithCoupon);
        }
    }, [claimedCoupon?.ResellerInventoryItem, couponCode]);

    const { data: customer, dispatchApiRequest: getCustomer } =
        useSwitcherClient((client) => client.stripe_GetCustomer, {
            requestImmediately: true,
            onError: (e) => {
                rollbar.error(
                    "Error getting user stripe customer subscription info",
                    e
                );
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "errors:subscription-info-error"
                    })
                );
            },
            onSuccess: async (data) => {
                const isLapsedSub =
                    userInfo.Claims?.Details?.Roles?.includes("Lapsed") &&
                    userInfo?.Plan?.Details?.Status !== StripeStatus.Incomplete;
                const isTrialSub =
                    userInfo.Claims?.Details?.Roles?.includes("Trial");
                const isShopifyCustomer =
                    userInfo?.ActiveProduct?.Details?.Discriminator?.includes(
                        "ShopifyProduct"
                    );

                if (
                    !data ||
                    ((isLapsedSub || isTrialSub) &&
                        data.StripeCreditCards?.length === 0 &&
                        !isShopifyCustomer)
                ) {
                    navigate("/subscription");
                } else {
                    data.StripeCreditCards?.sort((a, b) =>
                        a.Default === b.Default ? 0 : a.Default ? -1 : 1
                    );
                    const activeSub = data?.StripeSubscriptions.find(
                        (s) =>
                            s.Status === StripeStatus.Active ||
                            s.Status === StripeStatus.Trialing
                    );
                    setHasActiveSub(!!activeSub);

                    if (!!activeSub) {
                        getStripePrices([userInfo?.User?.Details?.Id]);
                        setPricesLoadingLocal(false);
                    }
                }
            }
        });

    const { data: entitlements, dispatchApiRequest: getEntitlements } =
        useSwitcherClient(
            (client) => client.userEntitlements_getActiveUserEntitlements,
            {
                suppressErrorBoundary: true,
                requestImmediately: true,
                args: [userInfo?.User?.Details?.Id]
            }
        );

    const { dispatchApiRequest: getInvoice } = useSwitcherClient(
        (client) => client.stripeInvoices_GetInvoice,
        { suppressErrorBoundary: true, requestImmediately: false }
    );

    const isAppleSub = useMemo(() => {
        return (
            userInfo.ActiveProduct?.Details?.Discriminator === "AppleProduct" &&
            userInfo?.Plan?.Details?.IsRecurring === true
        );
    }, [userInfo]);

    const licensedSubscription = useMemo(
        () =>
            customer?.StripeSubscriptions?.find(
                (s) => s.UsageType === "licensed"
            ),
        [customer]
    );

    const {
        /** This is the last subscription invoice Stripe tried to collect and failed. If this exists then the account is overdue */
        transformedData: lastAttemptedInvoice,
        dispatchApiRequest: refetchLastAttemptedInvoice
    } = useSwitcherClient(
        (client) => client.userSubscriptions_GetSubscription,
        {
            suppressErrorBoundary: true,
            requestImmediately: false,
            args: [
                userInfo?.User?.Details?.Id,
                licensedSubscription?.SilverSunnStripeSubscriptionId
            ],
            transformResponseData: async ({
                originalResponse: { LatestInvoiceId }
            }) => {
                const invoice = await getInvoice([
                    LatestInvoiceId,
                    userInfo?.User?.Details?.Id
                ]);

                if (
                    (invoice.payment_intent_status !== null && // is draft invoice
                        invoice.payment_intent_status !== "succeeded") || // has been paid
                    licensedSubscription?.Status === StripeStatus.Incomplete ||
                    licensedSubscription?.Status === StripeStatus.PastDue
                ) {
                    return invoice; // if there is a failed payment, return the invoice. This will trigger the "Past due" state and pay the invoice on checkout step
                } else {
                    return null;
                }
            }
        }
    );

    // Need to only get userSubscriptions_GetSubscription if the user has a licensed subscription
    useEffect(() => {
        if (!!licensedSubscription?.SilverSunnStripeSubscriptionId) {
            refetchLastAttemptedInvoice([
                userInfo?.User?.Details?.Id,
                licensedSubscription.SilverSunnStripeSubscriptionId
            ]);
        }
    }, [
        licensedSubscription,
        userInfo?.User?.Details?.Id,
        refetchLastAttemptedInvoice
    ]);

    // combined subscriptions, products and price
    useEffect(() => {
        if (!!customer && !!prices) {
            const detailedSubs: SubscriptionWithPrice[] =
                customer.StripeSubscriptions.map((sub) => {
                    const matchingPrice = prices.find(
                        (p) => p?.Id === sub?.PlanId
                    );
                    setStripePrice(matchingPrice);
                    return {
                        subscription: sub,
                        price: matchingPrice
                    };
                });
            setDetailedSubscriptions(detailedSubs);
        }
    }, [customer, prices]);

    const displayCost = useCallback(
        (amount?: number, showPeriodText?: boolean) => {
            let periodText = "";
            if (!!stripePrice && hasActiveSub) {
                periodText =
                    stripePrice.RecurringInterval === "year"
                        ? t("subscription:per-year")
                        : (stripePrice.RecurringIntervalCount ?? 1) === 1
                          ? t("subscription:per-month")
                          : t("subscription:per-count-months", {
                                count: stripePrice.RecurringIntervalCount
                            });
            }
            return `${((amount || 0) / 100)
                .toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD"
                })
                // Remove trailing .00 for whole dollar amounts
                .replace(/\.00$/, "")}${showPeriodText ? `${periodText}` : ""}`;
        },
        [stripePrice, hasActiveSub, t]
    );

    const _displayDate = (date?: string) => {
        return displayDate(date, i18n.language, "dddd, MMMM D, YYYY");
    };

    const refreshCustomer = useCallback(
        async (args: { message: string; showMessage?: boolean }) => {
            await dispatch(refreshToken());
            await dispatch(getUserInfo());

            await Promise.allSettled([
                dispatch(getUserOrgs()),
                dispatch(getDefaultUserOrg()),
                getCustomer(),
                getEntitlements(),
                refetchLastAttemptedInvoice()
            ]);

            // Clear preselected price and coupon code
            setPreselectedPrice(undefined);
            setCouponCode(undefined);

            // re-fetch data in the invoice table
            refreshInvoices();

            dispatch(
                addNotification({
                    type: NotificationType.Success,
                    message: args.message
                })
            );
        },
        [
            dispatch,
            getCustomer,
            getEntitlements,
            refetchLastAttemptedInvoice,
            refreshInvoices
        ]
    );

    const {
        data: refreshedCustomerData,
        dispatchApiRequest: getRefreshedCustomer
    } = useSwitcherClient((client) => client.stripe_GetCustomer, {
        suppressErrorBoundary: true,
        requestImmediately: false
    });

    const shouldDisableUpdatedSubPolling = useMemo(() => {
        return (
            !!refreshedCustomerData &&
            refreshedCustomerData?.StripeSubscriptions?.some((s) => {
                return s.PlanId !== pausedProductId;
            })
        );
    }, [refreshedCustomerData]);

    const {
        initialize: pollForUpdatedSubAfterReactivation,
        running: pollingForUpdatedSub
    } = useInterval(
        () => {
            getRefreshedCustomer();
        },
        500,
        {
            // Wait for first refresh and it should be enough to get the updated subscription
            predelay: 1000,
            runImmediately: false,
            // Wait for the subscription to update to stop polling
            disable: shouldDisableUpdatedSubPolling,
            // Timeout after ten seconds and error
            timeout: 10000,
            // Report error if polling times out
            onTimeout: () => {
                rollbar.error(
                    "Timeout exceeded when polling for release during pause"
                );
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t(
                            "subscription-page:pause-release-during-timeout-error"
                        )
                    })
                );
            },
            // Polling has succeeded and we can move forward with refresh
            onDisable: () => {
                refreshCustomer({
                    message: t("subscription-page:pause-release-during-success")
                });
            }
        }
    );

    const shouldDisableCancelledSubPolling = useMemo(() => {
        return (
            !!refreshedCustomerData &&
            (refreshedCustomerData.StripeSubscriptions?.length === 0 ||
                refreshedCustomerData?.StripeSubscriptions?.some((s) => {
                    return s.CancelAtPeriodEnd;
                }))
        );
    }, [refreshedCustomerData]);

    const {
        initialize: pollForUpdatedSubAfterCancel,
        running: pollingforCancelledSub
    } = useInterval(
        () => {
            getRefreshedCustomer();
        },
        500,
        {
            runImmediately: false,
            // Wait for no subscription to update to stop polling
            disable: shouldDisableCancelledSubPolling,
            // Timeout after ten seconds and error
            timeout: 10000,
            // Report error if polling times out
            onTimeout: () => {
                rollbar.error(
                    "Timeout exceeded when polling for cancel during pause"
                );
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t(
                            "subscription-page:pause-cancel-timeout-error"
                        )
                    })
                );
            },
            // Polling has succeeded and we can move forward with refresh
            onDisable: () => {
                refreshCustomer({
                    message: t("subscription-page:pause-cancel-success")
                });
            }
        }
    );

    useCheckoutRedirect({
        onSuccess: async ({ message }) => {
            try {
                await refreshCustomer({ message });
            } catch (e) {
                rollbar.error("Error refreshing user info", e);

                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("errors:user-info-load-error")
                    })
                );
            }
        },
        onFailure: () => {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: t("subscription-page:payment-error")
                })
            );
        }
    });

    const stripeErrorPassesFilter = (e: string) => {
        const isNotEmpty = !!e;
        const doesNotIncludeUrl = !e.includes("http");
        return isNotEmpty && doesNotIncludeUrl;
    };

    const openManagePlanModal = useCallback(() => {
        dispatch(
            setActiveModal({
                id: "manage-plan",
                type: Modals.ManagePlanModal,
                component: (
                    <SetupIntentElement>
                        <ManagePlanModal
                            preselectedPrice={preselectedPrice}
                            prices={filteredPrices}
                            couponCode={couponCode}
                            stripePricesLoading={pricesLoadingLocal}
                            customer={customer}
                            licensedSubscription={licensedSubscription}
                            isPastDue={
                                !!lastAttemptedInvoice ||
                                licensedSubscription?.Status ===
                                    StripeStatus.PastDue ||
                                licensedSubscription?.Status ===
                                    StripeStatus.PastDue
                            }
                            lastAttemptedInvoice={lastAttemptedInvoice}
                            onCheckoutSuccess={async ({
                                selectedPaymentMethodId
                            }) => {
                                // Set payment as primary if not already
                                if (
                                    !customer?.StripeCreditCards.find(
                                        (method) =>
                                            method.SilverSunnStripeCreditCardId ===
                                            selectedPaymentMethodId
                                    )?.Default
                                ) {
                                    await setPrimaryPaymentMethod([
                                        userInfo?.User?.Details?.Id,
                                        selectedPaymentMethodId
                                    ]);
                                }

                                refreshCustomer({
                                    message: t("subscription:success")
                                });
                            }}
                            onCheckoutError={async (
                                e,
                                { selectedPaymentMethodId, stripeError }
                            ) => {
                                let errorMessage = t(
                                    "subscription-page:payment-error"
                                );
                                const availablePaymentMethods =
                                    customer?.StripeCreditCards;
                                // If a new payment method is used, try to reset the user's default
                                // payment method and delete the new failed one.
                                if (
                                    selectedPaymentMethodId &&
                                    selectedPaymentMethodId !==
                                        "new-payment-method"
                                ) {
                                    try {
                                        if (availablePaymentMethods.length) {
                                            const originalDefault =
                                                availablePaymentMethods.find(
                                                    (c) =>
                                                        c.Default && !c.Expired
                                                );
                                            if (originalDefault) {
                                                await setPrimaryPaymentMethod([
                                                    userInfo?.User?.Details?.Id,
                                                    originalDefault.SilverSunnStripeCreditCardId
                                                ]);
                                            }
                                        }

                                        if (
                                            selectedPaymentMethodId !==
                                            "new-payment-method"
                                        ) {
                                            // If the existing payment method fails, show a more specific error message
                                            // (as long as it doesn't have a URL in it)
                                            if (stripeErrorPassesFilter(e))
                                                errorMessage = e;

                                            // Try to delete this payment method
                                            await deletePaymentMethod([
                                                userInfo?.User?.Details?.Id,
                                                selectedPaymentMethodId
                                            ]);
                                        }
                                    } catch {
                                        /** eat DeletePaymentMethod request errors */
                                    }
                                }

                                if (!!stripeError?.decline_code) {
                                    rollbar.warning(
                                        "Payment collection error with decline code",
                                        e
                                    );
                                } else {
                                    /*
                                     * Decline code is absent for one of two reasons:
                                     *
                                     * 1. The card issuer did not give Stripe enough information
                                     *      about the declination for them to pass on to us.
                                     * 2. A process failed somewhere up the chain without
                                     *      a "message" property on the error object
                                     *
                                     * Either way, reporting this error to Rollbar without
                                     * any context does us no practical good, so we'll ignore it.
                                     */
                                }

                                dispatch(
                                    addNotification({
                                        type: NotificationType.Danger,
                                        message: errorMessage
                                    })
                                );
                            }}
                            onPause={() => {
                                refreshCustomer({
                                    message: t(
                                        "subscription-page:pause-success"
                                    )
                                });
                            }}
                            onPauseUpdate={() => {
                                refreshCustomer({
                                    message: t(
                                        "subscription-page:pause-update-success"
                                    )
                                });
                            }}
                            onCancelPause={async (pauseAction: PauseAction) => {
                                if (
                                    pauseAction ===
                                    PauseAction.CancelPauseBefore
                                ) {
                                    refreshCustomer({
                                        message: t(
                                            "subscription-page:pause-release-before-success"
                                        )
                                    });
                                } else {
                                    pollForUpdatedSubAfterReactivation();
                                }
                            }}
                            onCancelPlan={async () => {
                                pollForUpdatedSubAfterCancel();
                            }}
                            onPauseError={(e, pauseAction) => {
                                rollbar.error(
                                    `Error managing subscription pausing (${pauseAction})`,
                                    e
                                );

                                const pauseErrorMessages = {
                                    [PauseAction.Pause]:
                                        "subscription-page:pause-error",
                                    [PauseAction.UpdatePause]:
                                        "subscription-page:pause-update-error",
                                    [PauseAction.CancelPauseBefore]:
                                        "subscription-page:pause-release-before-error",
                                    [PauseAction.CancelPauseDuring]:
                                        "subscription-page:pause-release-during-error",
                                    [PauseAction.CancelPlan]:
                                        "subscription-page:pause-cancel-error"
                                };

                                const errorMessage =
                                    pauseErrorMessages[pauseAction] ||
                                    "subscription-page:pause-generic-error";

                                dispatch(
                                    addNotification({
                                        type: NotificationType.Danger,
                                        message: t(errorMessage)
                                    })
                                );
                            }}
                        />
                    </SetupIntentElement>
                )
            })
        );
    }, [
        dispatch,
        preselectedPrice,
        filteredPrices,
        couponCode,
        pricesLoadingLocal,
        customer,
        licensedSubscription,
        lastAttemptedInvoice,
        refreshCustomer,
        setPrimaryPaymentMethod,
        userInfo?.User?.Details?.Id,
        t,
        deletePaymentMethod,
        pollForUpdatedSubAfterReactivation,
        pollForUpdatedSubAfterCancel
    ]);

    const modalSessionRef = React.useRef<boolean>(false);

    // Reset modal session flag when hash changes to #openModal
    useEffect(() => {
        // If hash changes to #openModal, allow modal to be opened again
        if (hash.includes("#openModal")) {
            modalSessionRef.current = false;
        }
    }, [hash]);

    // Open modal when conditions are met
    useEffect(() => {
        // Wait for required data and ensure we haven't already handled this session
        if (filteredPrices && customer && !modalSessionRef.current) {
            // Check if any trigger condition is present
            const shouldOpenModal =
                hash.includes("#openModal") ||
                hideSubscriptionPlans ||
                !!preselectedPrice ||
                !!couponCode;

            if (shouldOpenModal) {
                // Open modal and prevent reopening
                openManagePlanModal();

                // Clear the hash and query to prevent reopening on page refresh
                navigate("", {
                    replace: true
                });
                modalSessionRef.current = true;
            }
        }
    }, [
        openManagePlanModal,
        hash,
        filteredPrices,
        hideSubscriptionPlans,
        preselectedPrice,
        couponCode,
        customer,
        navigate
    ]);

    return (
        <LoadingOverlay
            isLoading={
                pricesLoadingLocal ||
                pollingForUpdatedSub ||
                pollingforCancelledSub
            }
        >
            <div className="row">
                <div className={`${styles["subscription-page-wrapper"]} col`}>
                    {isAppleSub ? (
                        <div className={styles["current-plan-row"]}>
                            <p>
                                {t(
                                    "subscription-page:apple-subscription-message"
                                )}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className={styles["current-plan-row"]}>
                                {!hasActiveSub && entitlements?.length ? (
                                    <EntitlementGrid
                                        entitlements={entitlements}
                                        openManagePlanModal={
                                            openManagePlanModal
                                        }
                                    />
                                ) : (
                                    <SubscriptionGrid
                                        subscriptions={detailedSubscriptions}
                                        isPastDue={!!lastAttemptedInvoice}
                                        displayCostFn={displayCost}
                                        openManagePlanModal={
                                            openManagePlanModal
                                        }
                                    />
                                )}
                                <NextPaymentOn
                                    customer={customer}
                                    refetchCustomer={getCustomer}
                                    isPastDue={!!lastAttemptedInvoice}
                                    upcomingDraftInvoice={upcomingDraftInvoice}
                                    currentEntitlement={last(entitlements)}
                                    activePlan={first(detailedSubscriptions)}
                                />
                            </div>
                            <LoadingOverlay
                                transparentOnLoad={true}
                                isLoading={invoicesLoading}
                                targetInteractiveCssSelector={
                                    ".invoice-table-element"
                                }
                            >
                                <InvoiceTable
                                    displayCostFn={displayCost}
                                    displayDateFn={_displayDate}
                                    invoices={paidInvoices}
                                    dateRangeSelectorComponent={
                                        InvoiceDateRangeSelectorComponent
                                    }
                                />
                            </LoadingOverlay>
                        </>
                    )}
                </div>
            </div>
        </LoadingOverlay>
    );
};
